import { CrawlDocumentStatus, CrawlRunStatus, CrawlStats, CrawlSummary } from '../types';

export const CRAWL_DURATION_WINDOW_MS = 600_000;

export const DOCUMENT_STATUS_ORDER: CrawlDocumentStatus[] = [
  'completed',
  'skipped',
  'failed',
  'pending',
  'processing',
];

function toTitleCase(value: string) {
  return value
    .split(/[_-\s]+/g)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

export function getRunStatusLabel(status: CrawlRunStatus) {
  return toTitleCase(status);
}

export function getDocumentStatusLabel(status: CrawlDocumentStatus) {
  return toTitleCase(status);
}

export function getTriggerLabel(triggerType: string | null | undefined) {
  if (!triggerType) {
    return 'Crawl';
  }

  return `${toTitleCase(triggerType)} Crawl`;
}

export function getDocumentStatusCount(stats: CrawlStats, status: CrawlDocumentStatus) {
  return stats.byStatus[status] ?? 0;
}

export function getStatusPercentage(count: number, total: number) {
  if (count <= 0 || total <= 0) {
    return '';
  }

  return `${((count / total) * 100).toFixed(1)}%`;
}

export function formatDurationSeconds(ms: number) {
  return `${(ms / 1000).toFixed(1)}s`;
}

export function formatDurationClock(ms: number) {
  const totalSeconds = ms / 1000;

  if (totalSeconds < 60) {
    return formatDurationSeconds(ms);
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds - minutes * 60;

  return `${minutes}m ${seconds.toFixed(1)}s`;
}

export function formatDurationNarrative(ms: number) {
  const totalSeconds = Math.max(1, Math.round(ms / 1000));

  if (totalSeconds < 60) {
    return `${totalSeconds} second${totalSeconds === 1 ? '' : 's'}`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  if (remainingSeconds === 0) {
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  return `${minutes} minute${minutes === 1 ? '' : 's'} ${remainingSeconds} second${
    remainingSeconds === 1 ? '' : 's'
  }`;
}

export function formatDurationProgress(ms: number, windowMs = CRAWL_DURATION_WINDOW_MS) {
  if (windowMs <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(100, (ms / windowMs) * 100));
}

function buildUtcParts(dateString: string | null) {
  if (!dateString) {
    return null;
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });

  return {
    time: `${time} UTC`,
    date: date.toISOString().slice(0, 10),
  };
}

export function formatUtcBlock(dateString: string | null) {
  const parts = buildUtcParts(dateString);

  if (!parts) {
    return {
      time: '-',
      date: '-',
    };
  }

  return parts;
}

export function formatUtcInline(dateString: string | null) {
  const parts = buildUtcParts(dateString);

  if (!parts) {
    return '-';
  }

  return `${parts.date} ${parts.time}`;
}

export function formatUpdatedLabel(dateString: string | null, now = Date.now()) {
  if (!dateString) {
    return 'Updated unavailable';
  }

  const timestamp = new Date(dateString).getTime();

  if (Number.isNaN(timestamp)) {
    return 'Updated unavailable';
  }

  const deltaMs = Math.max(0, now - timestamp);
  const minutes = Math.floor(deltaMs / 60_000);

  if (minutes <= 1) {
    return 'Updated just now';
  }

  if (minutes < 60) {
    return `Updated ${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return `Updated ${hours}h ago`;
  }

  return `Updated on ${formatUtcBlock(dateString).date}`;
}

export function buildHeroSummary(summary: CrawlSummary) {
  const { stats, caseId, lastCrawl } = summary;
  const completed = getDocumentStatusCount(stats, 'completed');
  const skipped = getDocumentStatusCount(stats, 'skipped');
  const failed = getDocumentStatusCount(stats, 'failed');
  const trigger = getTriggerLabel(lastCrawl?.triggerType).toLowerCase();
  const openingClause =
    lastCrawl?.status === 'completed'
      ? `A ${trigger} completed successfully for the ${caseId} case.`
      : lastCrawl?.status === 'failed'
        ? `A ${trigger} failed for the ${caseId} case.`
        : `A ${trigger} is in progress for the ${caseId} case.`;
  const completionClause =
    lastCrawl?.status === 'completed'
      ? `and the crawl finished in ${formatDurationNarrative(stats.totalDurationMs)}.`
      : lastCrawl?.status === 'failed'
        ? 'and the crawl ended before completing the full run.'
        : `and it has been running for ${formatDurationNarrative(stats.totalDurationMs)}.`;

  return `${openingClause} Of ${stats.totalDocuments} documents in the corpus, ${completed} ${
    completed === 1 ? 'was' : 'were'
  } processed and ${
    stats.documentsReplaced
  } ${stats.documentsReplaced === 1 ? 'was' : 'were'} replaced, with ${skipped} skipped as unchanged. ${
    failed === 0
      ? 'No failures were recorded'
      : `${failed} failure${failed === 1 ? ' was' : 's were'} recorded`
  } ${completionClause}`;
}
